// A publisher can pass `anchor` (an element or CSS selector) to showPaywall,
// openCheckout or login so the dialog centers over that element (e.g. the video
// it unlocks) instead of the viewport. Held for the whole modal, since each
// checkout step mounts its own CheckoutShell.

export type DialogAnchor = string | Element | null | undefined;

let current: DialogAnchor = null;

export function setDialogAnchor(anchor: DialogAnchor): void {
  current = anchor ?? null;
}

// The anchor in a storable form (selectors only), for a parked checkout.
export function dialogAnchorSelector(): string | undefined {
  return typeof current === 'string' ? current : undefined;
}

// A parked value is untrusted storage; anything but a string means no anchor.
export function parkedDialogAnchor(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

export function resolveDialogAnchor(): Element | null {
  if (!current) return null;
  if (typeof current === 'string') {
    try {
      return document.querySelector(current);
    } catch {
      // An invalid selector falls back to viewport centering.
      return null;
    }
  }
  return current.isConnected ? current : null;
}

interface Rect { left: number; top: number; width: number; height: number }
interface Size { width: number; height: number }

// Phones get the full-width sheet layout; offsetting it gains nothing.
export const anchorMinViewportWidth = 761;
// Space kept between the dialog and the viewport edge.
const edgeGap = 16;

function axisOffset(anchorStart: number, anchorSize: number, box: number, viewport: number): number {
  const room = (viewport - box) / 2 - edgeGap;
  if (room <= 0) return 0;
  const want = anchorStart + anchorSize / 2 - viewport / 2;
  return Math.round(Math.min(room, Math.max(-room, want)));
}

// The shift that moves a viewport-centered dialog of size `box` onto the
// anchor's center, clamped so the dialog stays fully on screen. Zero when the
// anchor is off screen or the viewport is phone-sized.
export function anchorOffset(anchor: Rect, box: Size, viewport: Size): { x: number; y: number } {
  const visible = anchor.width > 0 && anchor.height > 0
    && anchor.left < viewport.width && anchor.left + anchor.width > 0
    && anchor.top < viewport.height && anchor.top + anchor.height > 0;
  if (!visible || viewport.width < anchorMinViewportWidth) return { x: 0, y: 0 };
  return {
    x: axisOffset(anchor.left, anchor.width, box.width, viewport.width),
    y: axisOffset(anchor.top, anchor.height, box.height, viewport.height),
  };
}
